import {
  Box,
  Fade,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'lib/hooks/use-boolean'
import { useRef } from 'react'
import { LOCALS } from 'lib/i18next/types'
import rusFlag from 'assets/icons/flags/russian-flag.png'
import ukrFlag from 'assets/icons/flags/ukrainian-flag.png'

export const LanguageSelector = () => {
  const { t, i18n } = useTranslation()
  const open = useBoolean(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const currentLanguage =
    i18n.language === LOCALS.RU || i18n.language === LOCALS.RU_RU
      ? LOCALS.RU_RU
      : LOCALS.UK

  const options = [
    { value: LOCALS.UK, label: t('uk'), image: ukrFlag },
    { value: LOCALS.RU_RU, label: t('ru'), image: rusFlag },
  ]

  const handleChange = (code: LOCALS) => {
    if (currentLanguage !== code) {
      i18n.changeLanguage(code)
      open.setFalse()
    }
  }

  return (
    <Box
      ref={anchorRef}
      onMouseEnter={open.setTrue}
      onMouseLeave={open.setFalse}
      sx={(theme) => ({
        position: 'relative',
        flexGrow: 0,
        paddingX: '5px',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#c8e6c9',
          borderRadius: theme.shape.borderRadius,
        },
      })}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: 'center', height: '33px' }}
      >
        <img
          src={currentLanguage === LOCALS.UK ? ukrFlag : rusFlag}
          style={{
            width: '20px',
            borderRadius: '50%',
          }}
          alt="Country flag"
        />
        <Typography color="success" fontWeight={700}>
          {t(currentLanguage)}
        </Typography>
      </Stack>
      <Popper
        open={open.isTrue}
        anchorEl={anchorRef.current}
        placement="bottom"
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper
              sx={(theme) => ({
                '&.MuiPaper-elevation.MuiPaper-rounded': {
                  borderRadius: theme.shape.borderRadius.toString(),
                },
              })}
            >
              <MenuList
                id="language-list"
                aria-labelledby="language-list"
                sx={{
                  overflow: 'hidden',
                  borderRadius: '6px',
                }}
              >
                {options.map(({ value, label, image }) => {
                  const match = currentLanguage === value

                  return (
                    <MenuItem
                      key={value}
                      onClick={() => handleChange(value)}
                      sx={(theme) => ({
                        backgroundColor: match
                          ? theme.palette.action.selected
                          : undefined,
                        '&:hover': {
                          backgroundColor: match
                            ? theme.palette.action.selected
                            : theme.palette.action.hover,
                        },
                      })}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <img
                          src={image}
                          style={{
                            width: '20px',
                            borderRadius: '50%',
                          }}
                          alt="Country flag"
                        />
                        <Typography
                          sx={(theme) => ({
                            color: match
                              ? theme.palette.primary.main
                              : undefined,
                          })}
                        >
                          {label}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  )
                })}
              </MenuList>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  )
}
